import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CASH_MOVEMENT_KINDS, CURRENCIES, formatMoney, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Button, Dialog, Field, FormError, Select, Spinner, TextInput, fieldErrors, useToast } from '../../ui';
import { MoneyInput } from '../../ui/inputs';

export interface ShiftCashLine {
  currency: string;
  openingFloatCents: number;
  cashSalesCents: number;
  changeGivenCents: number;
  cashRefundsCents: number;
  accountPaymentsCents: number;
  depositsCents: number;
  depositRefundsCents: number;
  payInsCents: number;
  payOutsCents: number;
  dropsCents: number;
  expensesCents: number;
  supplierPaymentsCents: number;
  expectedCents: number;
  countedCents: number | null;
  varianceCents: number | null;
}

export interface ShiftDetail {
  id: string;
  number: string;
  status: 'open' | 'closed' | 'reconciled';
  branchId: string;
  tillId: string;
  cashierId: string;
  openedAt: string;
  closedAt: string | null;
  closeNote: string | null;
  reconciledAt: string | null;
  reconcileNote: string | null;
  reconciledByName: string | null;
  tillName: string;
  branchName: string;
  cashierName: string;
  blind: boolean;
  cash: ShiftCashLine[];
  nonCash: { method: string; currency: string; totalCents: number }[];
  saleCount: number;
  voidedCount: number;
  salesTotalCents: number;
  movements: { id: string; kind: keyof typeof CASH_MOVEMENT_KINDS; currency: string; amountCents: number; reason: string; createdAt: string; userName: string }[];
}

export function useCurrentShift(branchId: string | undefined) {
  const { canAny } = useSession();
  // Only people who run a till have a shift of their own (accountants and supervisors may not).
  return useQuery({
    queryKey: ['current-shift', branchId],
    queryFn: () => api.get<ShiftDetail | null>(`/api/shifts/current?branchId=${branchId}`),
    enabled: !!branchId && canAny('shifts.operate', 'pos.sell'),
  });
}

export function OpenShiftCard({ branchId }: { branchId: string }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const tills = useQuery({
    queryKey: ['tills', branchId],
    queryFn: () => api.get<{ id: string; name: string; isActive: boolean; openShift: { cashierName: string } | null }[]>(`/api/tills?branchId=${branchId}`),
  });
  const [tillId, setTillId] = useState('');
  const currencies = me.business.acceptedCurrencies as CurrencyCode[];
  const [floats, setFloats] = useState<Record<string, number | null>>({});
  const free = (tills.data ?? []).filter((t) => t.isActive && !t.openShift);
  const chosen = tillId || free[0]?.id || '';
  const m = useMutation({
    mutationFn: () =>
      api.post<ShiftDetail>('/api/shifts', {
        tillId: chosen,
        floats: Object.entries(floats)
          .filter(([, v]) => v != null)
          .map(([currency, amountCents]) => ({ currency, amountCents })),
      }),
    onSuccess: (s) => {
      toast(`Shift ${s.number} open on ${s.tillName}`);
      void qc.invalidateQueries({ queryKey: ['current-shift'] });
    },
  });
  return (
    <div className="mx-auto max-w-lg rounded-[var(--radius-panel)] border border-line bg-surface p-6">
      <h2 className="text-xl font-semibold">Open your till</h2>
      <p className="mt-1 text-muted">Count the float in the drawer before you start. Everything you take is checked against it when you close.</p>
      {tills.isLoading && <Spinner />}
      <FormError error={m.error} />
      {tills.data && free.length === 0 && (
        <p className="mt-4 rounded-[8px] bg-warn-soft px-3 py-2 text-sm text-warn">
          Every till here is in use: {(tills.data ?? []).filter((t) => t.openShift).map((t) => `${t.name} (${t.openShift!.cashierName})`).join(', ')}. Ask a manager to add a till or close one.
        </p>
      )}
      {free.length > 0 && (
        <div className="mt-5 space-y-4">
          <Field label="Till" htmlFor="till">
            <Select id="till" value={chosen} onChange={(e) => setTillId(e.target.value)}>
              {free.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            {currencies.map((c) => (
              <Field key={c} label={`Float in ${CURRENCIES[c].name}`} htmlFor={`float-${c}`}>
                <MoneyInput id={`float-${c}`} currency={c} value={floats[c] ?? null} onChange={(v) => setFloats({ ...floats, [c]: v })} placeholder="0.00" />
              </Field>
            ))}
          </div>
          <Button variant="primary" className="w-full" busy={m.isPending} onClick={() => m.mutate()}>
            Open till
          </Button>
        </div>
      )}
    </div>
  );
}

export function CashMovementDialog({ shift, onClose }: { shift: ShiftDetail; onClose: () => void }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<keyof typeof CASH_MOVEMENT_KINDS>('drop');
  const [currency, setCurrency] = useState(me.business.baseCurrency);
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/shifts/${shift.id}/cash`, { kind, currency, amountCents: amount, reason }),
    onSuccess: () => {
      toast(`${CASH_MOVEMENT_KINDS[kind]}: ${formatMoney(amount ?? 0, currency as CurrencyCode)}`);
      void qc.invalidateQueries({ queryKey: ['current-shift'] });
      void qc.invalidateQueries({ queryKey: ['shift', shift.id] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Cash in or out of the drawer"
      description="Record every note that goes in or out other than sales, so the count at closing matches."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!amount || reason.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>Record</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What happened" htmlFor="cm-kind">
          <Select id="cm-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="drop">Took cash to the safe</option>
            <option value="pay_out">Paid cash out (petty cash)</option>
            <option value="pay_in">Added cash (more change)</option>
          </Select>
        </Field>
        <Field label="Currency" htmlFor="cm-cur">
          <Select id="cm-cur" value={currency} onChange={(e) => setCurrency(e.target.value as CurrencyCode)}>
            {me.business.acceptedCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Amount" htmlFor="cm-amt" error={f.amountCents}>
          <MoneyInput id="cm-amt" currency={currency as CurrencyCode} value={amount} onChange={setAmount} autoFocus />
        </Field>
        <Field label="Reason" htmlFor="cm-reason" error={f.reason}>
          <TextInput id="cm-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Midday drop" />
        </Field>
      </div>
    </Dialog>
  );
}

export function CloseShiftDialog({ shift, onClose }: { shift: ShiftDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const currencies = shift.cash.map((c) => c.currency as CurrencyCode);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [note, setNote] = useState('');
  const ready = currencies.every((c) => counts[c] != null);
  const m = useMutation({
    mutationFn: () =>
      api.post<ShiftDetail>(`/api/shifts/${shift.id}/close`, {
        counts: currencies.map((c) => ({ currency: c, countedCents: counts[c] ?? 0 })),
        note: note || null,
      }),
    onSuccess: (s) => {
      const off = s.cash.filter((c) => c.varianceCents);
      toast(off.length ? `Shift closed. ${off.map((c) => `${c.currency} ${c.varianceCents! < 0 ? 'short' : 'over'} ${formatMoney(Math.abs(c.varianceCents!), c.currency as CurrencyCode)}`).join(', ')}` : 'Shift closed. The drawer balanced.');
      void qc.invalidateQueries({ queryKey: ['current-shift'] });
      void qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.setQueryData(['shift', shift.id], s);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Close ${shift.number}`}
      description="Count every note and coin in the drawer, including the float, and enter the totals."
      footer={
        <>
          <Button onClick={onClose}>Keep selling</Button>
          <Button variant="primary" disabled={!ready} busy={m.isPending} onClick={() => m.mutate()}>Close shift</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        {currencies.map((c) => (
          <Field key={c} label={`${CURRENCIES[c].name} counted`} htmlFor={`count-${c}`} hint={shift.blind ? undefined : `System expects ${formatMoney(shift.cash.find((x) => x.currency === c)!.expectedCents, c)}`}>
            <MoneyInput id={`count-${c}`} currency={c} value={counts[c] ?? null} onChange={(v) => setCounts({ ...counts, [c]: v })} />
          </Field>
        ))}
      </div>
      <Field label="Note (optional)" htmlFor="close-note" className="mt-4">
        <TextInput id="close-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}
